import { MaterialIcon } from '@/ui/material-icon'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { Label } from '@/ui/label'
import styles from './ConfigureDataDestinationSheet.module.scss'

type LoadMode = 'full' | 'incremental'
type ScdType = 'scd_1' | 'scd_2'

type LoadTypeSettingsProps = {
  isOpen: boolean
  isSaving: boolean
  loadMode: LoadMode
  scdType: ScdType
  deleteIdentifierColumn: string
  createdDateColumn: string
  modifiedDateColumn: string
  outputPreviewColumnOptions: string[]
  onToggle: () => void
  onOpenPrerequisites: () => void
  onSave: () => void
  onLoadModeChange: (mode: LoadMode) => void
  onScdTypeChange: (type: ScdType) => void
  onDeleteIdentifierColumnChange: (value: string) => void
  onCreatedDateColumnChange: (value: string) => void
  onModifiedDateColumnChange: (value: string) => void
}

export function LoadTypeSettings({
  isOpen,
  isSaving,
  loadMode,
  scdType,
  deleteIdentifierColumn,
  createdDateColumn,
  modifiedDateColumn,
  outputPreviewColumnOptions,
  onToggle,
  onOpenPrerequisites,
  onSave,
  onLoadModeChange,
  onScdTypeChange,
  onDeleteIdentifierColumnChange,
  onCreatedDateColumnChange,
  onModifiedDateColumnChange,
}: Readonly<LoadTypeSettingsProps>) {
  return (
    <div className={styles.accordion}>
      <button
        type="button"
        className={styles.accordionHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span>Load Type</span>
        <MaterialIcon name={isOpen ? 'expand_more' : 'chevron_right'} size={20} />
      </button>
      {isOpen && (
        <div className={styles.accordionBody}>
          <div className={styles.cdcRow}>
            <div className={styles.cdcIcon}>
              <MaterialIcon name="sync_alt" size={28} />
            </div>
            <div>
              <div className={styles.cdcTitle}>CDC Type</div>
              <button
                type="button"
                className={styles.prereqLink}
                onClick={onOpenPrerequisites}
              >
                <MaterialIcon name="warning" size={16} aria-hidden />
                Prerequisites
              </button>
            </div>
          </div>

          <div className={styles.radioRow}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="load-mode"
                checked={loadMode === 'full'}
                onChange={() => onLoadModeChange('full')}
              />
              {' '}
              Full load
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="load-mode"
                checked={loadMode === 'incremental'}
                onChange={() => onLoadModeChange('incremental')}
              />
              {' '}
              Incremental Load
            </label>
          </div>

          {loadMode === 'incremental' && (
            <div className={styles.incrementalFields}>
              <div className={styles.field}>
                <Label className={styles.fieldLabel}>SCD Type</Label>
                <Select
                  value={scdType}
                  onValueChange={value => onScdTypeChange(value === 'scd_2' ? 'scd_2' : 'scd_1')}
                >
                  <SelectTrigger className={styles.textInput}>
                    <SelectValue placeholder="Select SCD Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scd_1">SCD 1</SelectItem>
                    <SelectItem value="scd_2">SCD 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className={styles.field}>
                <Label className={styles.fieldLabel}>Delete Identifier</Label>
                <Select
                  value={deleteIdentifierColumn}
                  onValueChange={onDeleteIdentifierColumnChange}
                >
                  <SelectTrigger className={styles.textInput}>
                    <SelectValue placeholder="Delete Identifier Column" />
                  </SelectTrigger>
                  <SelectContent>
                    {outputPreviewColumnOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className={styles.field}>
                <Label className={styles.fieldLabel}>Created Date</Label>
                <Select
                  value={createdDateColumn}
                  onValueChange={onCreatedDateColumnChange}
                >
                  <SelectTrigger className={styles.textInput}>
                    <SelectValue placeholder="Select Created datetime" />
                  </SelectTrigger>
                  <SelectContent>
                    {outputPreviewColumnOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className={styles.field}>
                <Label className={styles.fieldLabel}>Modified Date</Label>
                <Select
                  value={modifiedDateColumn}
                  onValueChange={onModifiedDateColumnChange}
                >
                  <SelectTrigger className={styles.textInput}>
                    <SelectValue placeholder="Select Modified datetime" />
                  </SelectTrigger>
                  <SelectContent>
                    {outputPreviewColumnOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
